import React, { useState, useEffect } from "react";
import "./CourseEditModal.css";
import { apiFetch } from "../api";

export default function CourseEditModal2({ data, season = "", onClose, onSave }) {
  /* ---------- 1. Grund-State ---------- */
  const empty = {
    id: "",
    title: "",
    semester: season,
    courseSemester: "",
    degree: "",
    application: "",
    weekday: "",
    timeFrom: "",
    timeTo: "",
    repeatMode: "single",   // 'single' | 'weekly' | 'daily'
    date: "",               // für single
    dateFrom: "",           // für weekly/daily
    dateTo: "",             // für weekly/daily
    code: "",
    location: "",
    language: "",
    origin: "",
    lecturer: "",
    description: "",
    requirement: false,
    excludedDates: ""
  };

  const WEEKDAYS = [
    "Montag",
    "Dienstag",
    "Mittwoch",
    "Donnerstag",
    "Freitag",
    "Samstag"
  ];

  const [form, setForm] = useState(empty);

  /* ---------- 2. Datensatz komplett laden ---------- */
  useEffect(() => {
    if (!data?.id) return;

    apiFetch(`get_course_details_full.php?id=${data.id}`)
      .then(r => r.json())
      .then(row => {
        if (!row?.id) throw new Error("keine Daten");

        let repeatMode = "single";
        if (row.course_date === "weekly") repeatMode = "weekly";
        else if (row.course_date === "block") repeatMode = "daily";

        setForm({
          id:               row.id,
          title:            row.course_title ?? "",
          semester:         row.course_semester_description || season || "",
          courseSemester:   row.course_semester !== null ? String(row.course_semester) : "",
          degree:           row.course_degree ?? "",
          application:      row.course_application ?? "",
          weekday:          row.course_weekday ?? "",
          timeFrom:         row.course_start ?? "",
          timeTo:           row.course_stop ?? "",

          repeatMode,
          date:             (repeatMode === "single" && row.course_date) ? row.course_date : "",
          dateFrom:         row.course_date_from ?? "",
          dateTo:           row.course_date_to ?? "",

          code:             row.course_code ?? "",
          location:         row.course_location ?? "",
          language:         row.course_language ?? "",
          origin:           row.course_origin ?? "",
          lecturer:         row.course_lecturer ?? "",
          description:      row.course_description ?? "",
          requirement:      !!row.course_requirement,
          excludedDates:    row.course_excluded ?? ""
        });
      })
      .catch(err => {
        console.error(err);
        alert("Fehler beim Laden der Kursdetails");
        onClose();
      });
  }, [data, season, onClose]);

  /* ---------- 3. Form-Handler ---------- */
  const onChange = key => e =>
    setForm(p => ({
      ...p,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value
    }));

  const onRepeatChange = e => {
    const mode = e.target.value;
    setForm(p => ({
      ...p,
      repeatMode: mode,
      ...(mode === "single" ? { dateFrom: "", dateTo: "" } : {}),
      ...(mode !== "single" ? { date: "" } : {})
    }));
  };

  /* ---------- 4. Speichern ---------- */
  const handleSubmit = async () => {
    const base = {
      id:             form.id,
      title:          form.title,
      semester:       form.semester,
      courseSemester: form.courseSemester,
      degree:         form.degree,
      application:    form.application,
      weekday:        form.weekday,
      timeFrom:       form.timeFrom,
      timeTo:         form.timeTo,
      code:           form.code,
      location:       form.location,
      language:       form.language,
      origin:         form.origin,
      lecturer:       form.lecturer,
      description:    form.description,
      requirement:    form.requirement ? 1 : 0,
      excludedDates:  form.excludedDates
    };

    if (form.repeatMode === "weekly") {
      base.isWeekly = true;
      base.dateFrom = form.dateFrom;
      base.dateTo   = form.dateTo;
    } else if (form.repeatMode === "daily") {
      base.date     = "block";
      base.dateFrom = form.dateFrom;
      base.dateTo   = form.dateTo;
    } else {
      base.date = form.date;
    }

    try {
      const res = await apiFetch("update_course.php", {
        method: "POST",
        body: JSON.stringify(base)
      });
      const js = await res.json().catch(() => ({}));
      alert(js.message || js.error || "Unbekannter Fehler");
      if (res.ok) {
        onSave?.();
        onClose();
      }
    } catch (e) {
      console.error(e);
      alert("Speichern fehlgeschlagen.");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Diesen Kurs-Termin unwiderruflich löschen?")) return;

    try {
      const res = await apiFetch("delete_course.php", {
        method: "POST",
        body: JSON.stringify({ id: form.id })
      });
      const js = await res.json().catch(() => ({}));
      alert(js.message || js.error || "Unbekannter Fehler");
      if (res.ok) {
        onSave?.();
        onClose();
      }
    } catch (e) {
      console.error(e);
      alert("Löschen fehlgeschlagen.");
    }
  };

  /* ---------- 5. UI ---------- */
  const rows = [
    ["Aktives Semester",  "semester",        "WS 24/25"],
    ["Kurstitel",         "title",           ""        ],
    ["Inhaltliches Sem.", "courseSemester",  "0-6"     ],
    ["Studiengang",       "degree",          ""        ],
    ["Anwendungsmodul",   "application",     ""        ],
    ["Wochentag",         "weekday",         "Montag", "select-weekday"],
    ["Beginn (HH:MM)",    "timeFrom",        "",       "time"],
    ["Ende (HH:MM)",      "timeTo",          "",       "time"],
    ["Raum / Ort",        "location",        ""        ],
    ["Dozent*in",         "lecturer",        ""        ],
    ["Kurscode",          "code",            ""        ],
    ["Sprache",           "language",        ""        ],
    ["Herkunft",          "origin",          ""        ],
    ["Beschreibung",      "description",     "",       "textarea"],
    ["Ausfalltermine (YYYY-MM-DD,…)", "excludedDates",""]
  ];

  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <h3>Kurs bearbeiten</h3>
        <input type="hidden" value={form.id} />

        {rows.map(([label, key, ph, type]) => (
          <React.Fragment key={key}>
            <label>{label}:</label>

            {type === "textarea" ? (
              <textarea
                rows="3"
                value={form[key]}
                onChange={onChange(key)}
              />
            ) : type === "select-weekday" ? (
              <select value={form[key]} onChange={onChange(key)}>
                <option value="">Bitte wählen</option>
                {WEEKDAYS.map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            ) : (
              <input
                type={type || "text"}
                value={form[key]}
                onChange={onChange(key)}
                placeholder={ph}
              />
            )}
          </React.Fragment>
        ))}

        {/* Wiederholung */}
        <label>Wiederholung:</label>
        <select value={form.repeatMode} onChange={onRepeatChange}>
          <option value="single">einmalig</option>
          <option value="weekly">wöchentlich</option>
          <option value="daily">täglich (Block)</option>
        </select>

        {/* Datum / Zeitraum je nach Modus */}
        {form.repeatMode === "single" ? (
          <>
            <label>Datum (YYYY-MM-DD):</label>
            <input type="date" value={form.date} onChange={onChange("date")} />
          </>
        ) : (
          <>
            <label>Von (YYYY-MM-DD):</label>
            <input type="date" value={form.dateFrom} onChange={onChange("dateFrom")} />
            <label>Bis (YYYY-MM-DD):</label>
            <input type="date" value={form.dateTo} onChange={onChange("dateTo")} />
          </>
        )}

        {/* Optionen */}
        <label>
          <input
            type="checkbox"
            checked={form.requirement}
            onChange={onChange("requirement")}
          />
          &nbsp;Verpflichtend
        </label>

        <div className="modal-actions">
          <button onClick={handleSubmit}>Speichern</button>
          <button className="danger" onClick={handleDelete}>Löschen</button>
          <button className="secondary" onClick={onClose}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
}